
import { createSelector } from 'reselect';
import { State } from 'reducers';
import { SystemTime, UtcTime } from 'types';

export const isLoading = (state: State) => state.server.loading;

export const getError = (state: State) => state.server.error;

export const getServerTimezone = (state: State) => state.server.timezone;

export const getReportsPath = (state: State) => state.server.reportsPath;

export const getReportsPort = (state: State) => state.server.reportsPort;

export const getReportsProtocol = (state: State) => state.server.reportsProtocol;

export const getReportTypes = (state: State) => state.server.reportTypes;

export const getAppVersion = (state: State) => state.server.appVersion;

export const getDevicePackVersion = (state: State) => state.server.devicePackVersion;

export const getCopyrightYear = (state: State) => state.server.copyrightYear;

export const getRefreshRate = (state: State) => state.server.refreshRate;

export const getAvailableLanguages = (state: State) => state.server.availableLanguages;

export const isUsingWebsocket = (state: State) => state.server.useWebsocket;

const constructUtcTime = (utcYear: number, bias: number, systemTime: SystemTime): number => {
    const oneDay = 24 * 60 * 60 * 1000;
    const utcDate = new Date();

    // Set the year and first day of the month
    utcDate.setUTCFullYear(systemTime.year || utcYear, systemTime.month - 1, 1);

    // Take the current day of the week for the first of the month
    const dayOfWeekOfDay1 = utcDate.getUTCDay();

    // Shift to the nth week of the month (note: weekOfMonth == 5 means the last occurrence in the month)
    const firstOfMonthShiftedNWeeks = utcDate.getUTCDate() + ((systemTime.weekOfMonth - 1) * 7);

    // Now adjust forwards/backwards to get to the right day of the week.
    // Account for negative delta (e.g. first day is a Thursday (4) but we want Sunday (0)... actually need to add -4 + 7 = 3 days)
    let delta = systemTime.dayOfWeek - dayOfWeekOfDay1;
    if (delta < 0) delta += 7;
    let almostRightDay = firstOfMonthShiftedNWeeks + delta;

    // Account for 'last occurence in month' when last occurrence is actually #4, not #5
    const tentativeMonth = new Date(utcDate.getTime() + almostRightDay * oneDay).getUTCMonth();
    if (tentativeMonth !== utcDate.getUTCMonth()) almostRightDay -= 7;

    // It's definitely the right day now.
    utcDate.setUTCDate(almostRightDay);

    // Set the hours and minutes (which are given relative to local time)
    // So also add the bias to get back to UTC
    utcDate.setUTCHours(systemTime.hour, systemTime.minute + bias, 0, 0);

    return utcDate.getTime();
};

// Given an input time (and stored timezone information from the server), calculate the
// timezone offset in minutes for the given time. (i.e. amount to add to UTC to get local time)
export const getTimezoneOffsetCalculator =
    createSelector(
        getServerTimezone,
        timezone => (utcTime: UtcTime) => {
            if (!timezone) {
                return utcTime;
            }

            const { bias = 0, standardBias, daylightBias, daylightStart, standardStart } = timezone;

            if (!daylightStart || !standardStart) {
                return -bias;
            }

            const utcYear = new Date(utcTime).getUTCFullYear();
            const utcStart = constructUtcTime(utcYear, bias + standardBias, daylightStart);
            let utcEnd = constructUtcTime(utcYear, bias + standardBias, standardStart);

            if (daylightStart.month > standardStart.month && utcTime > utcEnd) {
                utcEnd = constructUtcTime(utcYear + 1, bias + standardBias, standardStart);
            }

            const isDst = utcTime >= utcStart && utcTime < utcEnd;

            return -(bias + (isDst ? daylightBias : standardBias));
        }
    );



// WEBPACK FOOTER //
// ./src/selectors/serverSelectors.ts